// Seletor de cliente com busca — inclusive por CNPJ em dígitos.
//
// Multi-CNPJ (2026-08-04): a Hydrodata fatura parcelas de um mesmo contrato pra
// CNPJs diferentes (CERILUZ → três usinas; Nova Engevix/SENHA/GS Inima → nota
// direto pra concessionária). Achar a empresa certa numa lista de centenas
// exige buscar pelo CNPJ, que é como o cliente manda a instrução.
//
// Existiam três implementações: este <Select> (soterrado no modal de
// reorganizar parcelamento) e dois <select> nativos sem busca nenhuma nos
// modais do Contas a Receber. Esta é a boa, extraída pra valer nos três.
import { useEffect, useState } from 'react';
import { Select } from 'antd';
import { carregarClientesArt } from '../pages/operacional-contratos/utils';
import type { ClienteSlim } from '../pages/operacional-contratos/types';

export function SelectCliente({
  value, onChange, contratanteId, placeholder, size, allowClear = true, disabled, style,
  compacto = false,
}: {
  value?: number | null;
  onChange: (v: number | null) => void;
  /** Cliente do contrato. Marca a opção como padrão e ordena ela primeiro. */
  contratanteId?: number | null;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  allowClear?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  /**
   * Fechado, mostra só a razão social — o CNPJ continua no dropdown e na busca.
   * Em tabela apertada o rótulo cheio ("Razão Social Ltda · 00.000.000/0001-00")
   * espremia todas as outras colunas.
   */
  compacto?: boolean;
}) {
  const [clientes, setClientes] = useState<ClienteSlim[]>([]);
  // true = inclui contrapartes do Conta Azul (pagadores simples, ex.: Banco
  // Inter). Cache de 10 min compartilhado com o resto do sistema.
  useEffect(() => { carregarClientesArt(true).then(setClientes).catch(() => {}); }, []);

  const opcoes = clientes
    .slice()
    .sort((a, b) => {
      // Contratante primeiro: é o padrão, e quem abre o campo em geral quer
      // confirmar que está nele ou sair dele conscientemente.
      if (contratanteId) {
        if (a.id === contratanteId) return -1;
        if (b.id === contratanteId) return 1;
      }
      return a.razao_social.localeCompare(b.razao_social);
    })
    .map(c => ({
      value: c.id,
      // O nome fantasia entra no rótulo (Lucas, 31/08/2026): a Consplan tem um
      // CNPJ por empreendimento com a MESMA razão social, e o fantasia é o
      // nome do empreendimento — era a única coisa que distinguia as filiais, e
      // ficava só na busca, invisível. Tanto que alguém chegou a escrever o
      // empreendimento dentro da razão social pra conseguir enxergar.
      label: c.razao_social
        + (c.nome_fantasia && c.nome_fantasia.trim().toLowerCase() !== c.razao_social.trim().toLowerCase()
            ? ` (${c.nome_fantasia})` : '')
        + (c.cnpj ? ` · ${c.cnpj}` : ' · sem CNPJ')
        + (c.id === contratanteId ? ' (contratante do contrato)' : '')
        + (c.tipo_cadastro === 'contraparte' ? ' [CA]' : ''),
      curto: c.razao_social,
      title: c.razao_social + (c.cnpj ? ` · ${c.cnpj}` : ''),
      _busca: [c.razao_social, c.nome_fantasia, (c.cnpj || '').replace(/\D/g, '')]
        .filter(Boolean).join(' ').toLowerCase(),
    }));

  return (
    <Select
      size={size} showSearch allowClear={allowClear} disabled={disabled}
      value={value ?? undefined}
      onChange={(v) => onChange(v == null ? null : Number(v))}
      placeholder={placeholder}
      optionLabelProp={compacto ? 'curto' : 'label'}
      style={{ width: '100%', ...style }}
      filterOption={(input, opt) => {
        const q = (input || '').toLowerCase().trim();
        if (!q) return true;
        const alvo = ((opt as any)?._busca || '');
        if (alvo.includes(q)) return true;
        // Busca por CNPJ: a pessoa digita com ou sem máscara, comparamos só
        // dígitos. Exige 3+ pra não casar meia lista com "1".
        const qDigits = q.replace(/\D/g, '');
        return qDigits.length >= 3 && alvo.includes(qDigits);
      }}
      options={opcoes}
    />
  );
}
